import React from "react";
import { MoonLoader } from "react-spinners";
import { EnrichedAppointment, formatLocalTime } from "../../utils/schedule";
import type { ScheduleStatus } from "../../hooks/useProviderSchedule";
import { recordRecentPatient } from "../../utils/recentPatients";

// item.name falls back to the patientId (or "Unknown patient") when no real
// name is known — don't persist those as if they were a display name.
const recentPatientName = (item: EnrichedAppointment): string | undefined =>
  item.name && item.name !== item.patientId ? item.name : undefined;

interface RecentEncountersListProps {
  // Today's already-completed appointments, most-recent-first.
  encounters: EnrichedAppointment[];
  scheduleStatus: ScheduleStatus;
  onSelectPatient: (patientId: string, encounterId?: string) => void;
}

const RecentEncountersList: React.FC<RecentEncountersListProps> = ({
  encounters,
  scheduleStatus,
  onSelectPatient,
}) => {
  if (scheduleStatus === "loading") {
    return (
      <div className="flex items-center gap-3 text-sm text-gray-500">
        <MoonLoader size={16} color="#7a46bb" loading={true} />
        Loading…
      </div>
    );
  }

  if (encounters.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 border-dashed rounded-md p-6 text-center text-sm text-gray-500">
        No completed encounters yet today.
      </div>
    );
  }

  return (
    // Cap the visible height to ~5 rows; scroll within for the rest so a full
    // day of completed encounters doesn't grow the side column unbounded.
    <ul className="divide-y divide-gray-200 max-h-[26rem] overflow-y-auto -mr-2 pr-2">
      {encounters.map((item, index) => (
        <li
          key={
            item.appt.appointmentid ??
            item.appt.encounterid ??
            `${item.patientId ?? "x"}-${item.appt.starttime ?? index}`
          }
        >
          <button
            type="button"
            disabled={!item.patientId}
            onClick={() => {
              /* v8 ignore next -- button is disabled (unclickable) when patientId is absent */
              if (!item.patientId) return;
              recordRecentPatient(item.patientId, recentPatientName(item));
              onSelectPatient(item.patientId, item.encounterId);
            }}
            className="w-full text-left flex items-center gap-4 py-4 px-3 hover:bg-gray-50 cursor-pointer transition-colors focus:outline-none focus:bg-gray-50 rounded-md disabled:opacity-50 disabled:cursor-default"
          >
            <div className="flex-shrink-0 w-14 text-xs font-semibold text-gray-500 whitespace-nowrap">
              {item.start ? formatLocalTime(item.start) : "—"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {item.name}
              </p>
              <p className="text-xs text-gray-500 truncate">{item.apptType}</p>
              <p className="text-xs text-gray-400 truncate">
                Athena ID: {item.patientId ?? "N/A"}
              </p>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
};

export default RecentEncountersList;
